/* eslint max-len: 0 */ // --> OFF

import { Div } from '@expo/html-elements'

import { useAkteContext } from '../../../../../hooks/context/useAkteContext'
import type { StateHistoryEntry } from '../../../../../hooks/context/useAkteContext'

import { SubCard } from '../../../../../components/SubCard'

import { UebergabeRow } from './UebergabeRow'
import { UebergabeHeaderRow } from './UebergabeHeaderRow'
import { UebergabeSpacerRow } from './UebergabeSpacerRow'
import { useRelevantHandovers } from '../../hooks/useRelevantHandovers'
import { useMemo } from 'react'
import { getUebergabeTitleKey } from '../../uebergabeUi'

type Action =
  | "preRequest"
  | "preRequestRejected"
  | "request"
  | "approved"
  | "rejected"
  | "cancelled"
  | "returnFrom"

const allHandovers = [
  "Vertrieb2PE",
  "PE2Vertrieb",
  "Vertrieb2ENG",
  "ENG2Vertrieb",
  "CompletionOfPlanning",
  "Vertrieb2Projektleitung",
  "Projektleitung2SuW",
  "preliminaryHandover_Projektleitung2SuW",
  "finalHandover_Projektleitung2SuW",
  "SuW2Running",
] as const

export type Handover = (typeof allHandovers)[number]

export type HandoverEventData = {
  [processType in Handover]: {
    [action in Action]: undefined | null | StateHistoryEntry
  }
}

const allActions = [
  "preRequest",
  "preRequestRejected",
  "request",
  "approved",
  "rejected",
  "cancelled",
  "returnFrom",
] as const

const handoverSet = new Set<string>(allHandovers)
const actionSet = new Set<string>(allActions)

const transitionOverviewMappings: Record<string, [Handover, Action]> = {
  Projektleitung2SuW_preRequest: ["Projektleitung2SuW", "preRequest"],
  Projektleitung2SuW_request_old: ["Projektleitung2SuW", "request"],
  Projektleitung2SuW_preRequestRejected: ["Projektleitung2SuW", "preRequestRejected"],
  decision_UmsetungsplanungBeantragt_NEIN: ["PE2Vertrieb", "cancelled"],
  decision_UmsetungsBeantragt_NEIN: ["CompletionOfPlanning", "cancelled"],
  CompletionOfPlanning2Vertrieb_approved: ["CompletionOfPlanning", "approved"],
  CompletionOfPlanning2Vertrieb_rejected: ["CompletionOfPlanning", "rejected"],
  directFinalHandover_Projektleitung2SuW_request: ["finalHandover_Projektleitung2SuW", "request"],
  directFinalHandover_Projektleitung2SuW_approved: ["finalHandover_Projektleitung2SuW", "approved"],
  directFinalHandover_Projektleitung2SuW_rejected: ["finalHandover_Projektleitung2SuW", "rejected"],
  preliminaryHandover_Projektleitung2SuW_request: ["preliminaryHandover_Projektleitung2SuW", "request"],
  preliminaryHandover_Projektleitung2SuW_approved: ["preliminaryHandover_Projektleitung2SuW", "approved"],
  preliminaryHandover_Projektleitung2SuW_rejected: ["preliminaryHandover_Projektleitung2SuW", "rejected"],
  preliminaryToFinalHandover_Projektleitung2SuW_request: ["finalHandover_Projektleitung2SuW", "request"],
  preliminaryToFinalHandover_Projektleitung2SuW_approved: ["finalHandover_Projektleitung2SuW", "approved"],
  preliminaryToFinalHandover_Projektleitung2SuW_rejected: ["finalHandover_Projektleitung2SuW", "rejected"],
  finalHandover_Projektleitung2SuW_request: ["finalHandover_Projektleitung2SuW", "request"],
  finalHandover_Projektleitung2SuW_approved: ["finalHandover_Projektleitung2SuW", "approved"],
  finalHandover_Projektleitung2SuW_rejected: ["finalHandover_Projektleitung2SuW", "rejected"],
  GewaehrleistungsUebergabe_request: ["SuW2Running", "request"],
  GewaehrleistungsUebergabe_approved: ["SuW2Running", "approved"],
  GewaehrleistungsUebergabe_rejected: ["SuW2Running", "rejected"],
  Fremdbetrieb_request: ["SuW2Running", "request"],
  Fremdbetrieb_request_old: ["SuW2Running", "approved"],
  Fremdbetrieb_approved: ["SuW2Running", "approved"],
  Fremdbetrieb_rejected: ["SuW2Running", "rejected"],
  SuW_betrieb: ["SuW2Running", "approved"],
  SuWBetrieb_request: ["SuW2Running", "request"],
  SuWBetrieb_approved: ["SuW2Running", "approved"],
  SuWBetrieb_rejected: ["SuW2Running", "rejected"],
}

const stateOverviewMappings: Record<string, [Handover, Action]> = {
  handover_CompletionOfPlanning2Vertrieb: ["CompletionOfPlanning", "approved"],
  terminated_preUmsetzungsplanung: ["PE2Vertrieb", "cancelled"],
  terminated_preUmsetzung: ["CompletionOfPlanning", "cancelled"],
  handover_Projektleitung2SuW: ["Projektleitung2SuW", "request"],
  handover_directFinalHandover_Projektleitung2SuW: ["finalHandover_Projektleitung2SuW", "request"],
  handover_preliminaryHandover_Projektleitung2SuW: ["preliminaryHandover_Projektleitung2SuW", "request"],
  workOn_preliminaryToFinalHandover_Projektleitung2SuW: ["preliminaryHandover_Projektleitung2SuW", "approved"],
  handover_preliminaryToFinalHandover_Projektleitung2SuW: ["finalHandover_Projektleitung2SuW", "request"],
  workOn_finalHandover_Projektleitung2SuW: ["finalHandover_Projektleitung2SuW", "approved"],
  handover_terminated_inBetrieb: ["SuW2Running", "request"],
  terminated_inBetrieb: ["SuW2Running", "approved"],
  handover_GewaehrleistungsUebergabe: ["SuW2Running", "request"],
  handover_terminated_inFremdbetrieb: ["SuW2Running", "request"],
  terminated_inFremdbetrieb: ["SuW2Running", "approved"],
}

function emptyHandoverEventData() {
  return Object.fromEntries(
    allHandovers.map((handover) => [
      handover,
      {
        preRequest: null,
        preRequestRejected: null,
        request: undefined,
        approved: undefined,
        rejected: undefined,
        cancelled: undefined,
        returnFrom: undefined,
      },
    ])
  ) as HandoverEventData
}

function transitionToOverviewEvent(transitionName: string): [Handover, Action] | undefined {
  const mappedTransition = transitionOverviewMappings[transitionName]
  if (mappedTransition != undefined) return mappedTransition

  const transitionNameParts = transitionName.split("_")
  if (transitionNameParts.length == 2) {
    const [handoverStep, handoverAction] = transitionNameParts
    if (handoverSet.has(handoverStep) && actionSet.has(handoverAction)) {
      return [handoverStep as Handover, handoverAction as Action]
    }
  }

  if (transitionNameParts.length == 4 && transitionNameParts[0] == "returnFrom") {
    const handoverStep = transitionNameParts[3]
    if (handoverSet.has(handoverStep)) {
      return [handoverStep as Handover, "returnFrom"]
    }
  }

  return undefined
}

function handoverHasEvent(data: HandoverEventData[Handover]) {
  return Object.values(data).some((event) => event != undefined && event !== null)
}
   
export function SubCardUebergaben() {
  const akte = useAkteContext()

  const allRelevantHandovers = useRelevantHandovers()

  const stateHistoryForTitles = useMemo(
    () =>
      akte.stateHistory?.flatMap(({ state }) =>
        typeof state === 'string' ? [{ state }] : []
      ),
    [akte.stateHistory],
  )

  const handoverData = useMemo(() => {
    const tmpRes = emptyHandoverEventData()
    let newestHandover: number = allHandovers.length
    if(akte.stateHistory){
      for(const state of akte.stateHistory){
        if (state.transitionName == undefined) continue
        const overviewEvent = transitionToOverviewEvent(state.transitionName) ?? stateOverviewMappings[state.state]
        if (overviewEvent == undefined) continue
        const [handoverStep, handoverAction] = overviewEvent
        const handoverIdx = allHandovers.indexOf(handoverStep)
        if(handoverIdx >= 0 && handoverIdx <= newestHandover && tmpRes[handoverStep][handoverAction] === undefined){
          tmpRes[handoverStep][handoverAction] = state as StateHistoryEntry
          newestHandover = handoverIdx
        }
      }
    }
    return tmpRes
  }, [akte.stateHistory])

  const relevantHandovers = useMemo(
    () =>
      allHandovers.filter(
        (handover) => allRelevantHandovers.includes(handover) || handoverHasEvent(handoverData[handover])
      ),
    [allRelevantHandovers, handoverData],
  )

  if(handoverData == undefined) return <></>
  return (
    <>
      <SubCard title="Übergaben">
        <Div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <UebergabeHeaderRow
            headerCols={relevantHandovers}
            getHeaderTitleKey={(handover) => getUebergabeTitleKey(handover as Handover, stateHistoryForTitles)}
          />
          <UebergabeSpacerRow size="small" />
          <UebergabeRow
            handovers = {relevantHandovers}
            step = "preRequest"
            data={handoverData}
          />
          <UebergabeRow
            handovers = {relevantHandovers}
            step = "preRequestRejected"
            data={handoverData}
          />
          <UebergabeRow
            handovers = {relevantHandovers}
            step = "request"
            data={handoverData}
          />
          <UebergabeRow
            handovers = {relevantHandovers}
            step = "approved"
            data={handoverData}
          />
          <UebergabeRow
            handovers = {relevantHandovers}
            step = "rejected"
            data={handoverData}
          />
          <UebergabeRow
            handovers = {relevantHandovers}
            step = "cancelled"
            data={handoverData}
          />
          <UebergabeRow
            handovers = {relevantHandovers}
            step = "returnFrom"
            data={handoverData}
          />
          <UebergabeSpacerRow size="normal" />
        </Div>
      </SubCard>
    </>
  )
}
